import { useState, useEffect } from "react";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Clock, Database, Users, FileText, Loader2 } from "lucide-react";

type AnalysisStage = 
  | 'initializing'
  | 'finding_competitors'
  | 'scraping_profiles'
  | 'analyzing_employees'
  | 'detecting_ads'
  | 'generating_report'
  | 'completed';

interface AuditProgressIndicatorProps {
  status: string;
  createdAt: string;
  compact?: boolean;
}

export function AuditProgressIndicator({ status, createdAt, compact = false }: AuditProgressIndicatorProps) {
  const [stage, setStage] = useState<AnalysisStage>('initializing');
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    // Calculate duration
    const interval = setInterval(() => {
      const start = new Date(createdAt).getTime();
      const now = Date.now();
      setDuration(Math.floor((now - start) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [createdAt]);

  useEffect(() => {
    // Simulate progress based on duration (for display purposes)
    // In real implementation, this would come from polling get-audit-status
    if (status === 'processing') {
      const stages: AnalysisStage[] = [
        'initializing',
        'finding_competitors',
        'scraping_profiles',
        'analyzing_employees',
        'detecting_ads',
        'generating_report'
      ];
      
      const stageIndex = Math.min(Math.floor(duration / 20), stages.length - 1);
      setStage(stages[stageIndex]);
      setProgress(Math.min((stageIndex + 1) * 16.67, 100));
    }
  }, [duration, status]);

  const getStageIcon = (stage: AnalysisStage) => {
    switch (stage) {
      case 'finding_competitors':
        return <Database className="h-4 w-4" />;
      case 'scraping_profiles':
        return <Users className="h-4 w-4" />;
      case 'analyzing_employees':
        return <Users className="h-4 w-4" />;
      case 'detecting_ads':
        return <FileText className="h-4 w-4" />;
      case 'generating_report':
        return <FileText className="h-4 w-4" />;
      default:
        return <Loader2 className="h-4 w-4 animate-spin" />;
    }
  };

  const getStageLabel = (stage: AnalysisStage) => {
    switch (stage) {
      case 'initializing':
        return 'Inicjalizacja';
      case 'finding_competitors':
        return 'Wyszukiwanie konkurencji';
      case 'scraping_profiles':
        return 'Analiza profili';
      case 'analyzing_employees':
        return 'Analiza pracowników';
      case 'detecting_ads':
        return 'Wykrywanie reklam';
      case 'generating_report':
        return 'Generowanie raportu';
      default:
        return 'Przetwarzanie';
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {getStageIcon(stage)}
        <div className="flex-1 min-w-[100px]">
          <Progress value={progress} className="h-2" />
          <p className="text-xs text-muted-foreground mt-1">
            {Math.round(progress)}% • {formatDuration(duration)}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {getStageIcon(stage)}
        <Badge variant="outline" className="bg-blue-500/10 text-blue-700">
          {getStageLabel(stage)}
        </Badge>
      </div>
      <Progress value={progress} className="h-2" />
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{Math.round(progress)}%</span>
        <span className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {formatDuration(duration)}
        </span>
      </div>
    </div>
  );
}
